import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  ART_STYLE_EXTRACT_KIND,
  ART_STYLE_SAMPLE_KIND,
  fetchCreativeJobs,
  selectCreativeJobsMap,
  upsertJob,
  type CreativeJob,
} from '@/store/slices/creativeJobs';
import { fetchOwnerWallets } from '@/store/slices/wallet';

import { ART_STYLE } from '../../theme/tokens';
import {
  ART_STYLE_EXTRACT_PLACEMENT,
  ART_STYLE_SLOTS,
  MAX_MASCOTS,
  MAX_MASCOT_IMAGES,
  MAX_MOOD_IMAGES,
  activateArtStyle,
  describeArtStyleMascot,
  drawArtStyleSamples,
  extractArtStyle,
  fetchArtStyle,
  isMissingStyle,
  saveArtStyle,
  undoArtStyle,
  uploadArtStyleImage,
  type ArtStyle,
  type ArtStyleFailure,
  type ArtStyleImage,
  type ArtStyleImageKind,
  type ArtStyleMascot,
  type ArtStylePatch,
  type ArtStyleSlot,
} from './art-style-api';
import { ImagePrepFailure, prepareImage } from './image-prep';
import {
  ART_STYLE_RATING_NEEDED,
  EXTRACT_SAMPLES_NOT_STARTED,
  EXTRACT_SAMPLES_NO_CREDITS,
  EXTRACT_SAMPLES_QUEUED,
  extractFailedSince,
  extractLandedSince,
  isActiveJob,
  isExtractJobFor,
  isSampleJobFor,
  latestExtractJob,
  latestSampleJobs,
  previewsBehindKey,
  previewsView as previewsViewOf,
  startingSlots as startingSlotsOf,
  type PreviewsView,
} from './sample-jobs';

/** The extraction failures (an extract job's `error`, or a request's refusal) with copy of their own on the
 *  casino_creation pipeline: casino-creation refused the inputs (the same ones fail again), or it cannot be reached. */
const EXTRACT_REFUSED = 'art_style_extract_refused';
const PIPELINE_NOT_CONFIGURED = 'art_style_pipeline_not_configured';
/** A preview request refused while the newest batch still draws. */
const PREVIEWS_RUNNING = 'art_style_previews_running';
/** The refusal of a preview request for a style with no canonical text (a legacy style: extract it again). */
const NEEDS_DEFINITION = 'extract or write the style definition first';

/** Why the selected style could not be read: no service / route here, the style no longer exists, or a failed
 *  read. */
export type ArtStyleLoadFailure = 'unavailable' | 'missing' | 'failed';

/** Autosave state behind the header indicator. */
export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/** The one explicit request in flight (they all read the saved record, so they never overlap). `extract` covers
 *  only the request that queues an extraction: the extraction itself is a job (see {@link UseArtStyle.extracting}). */
export type ArtStyleBusy = 'extract' | 'undo' | 'activate' | 'samples' | null;

/** An image still uploading, shown as a pending thumbnail. */
export interface PendingImage {
  key: string;
  dataUri: string;
  kind: ArtStyleImageKind;
  /** A mascot image: the mascot it is for. */
  mascotId?: string;
}

export interface UseArtStyleOptions {
  tenantId: number | undefined;
  /** The selected style (validated against the brand's list by the caller). */
  styleId: string | undefined;
  /** The brand's existing logo, snapshotted onto the style as `logo_url` whenever the style is saved. */
  logoUrl: string | undefined;
  /** Show a (translated) message to the owner. */
  notify: (message: string) => void;
  /** The balance can't cover a draw: open Buy credits, preselecting the shortfall when known. */
  onNeedCredits: (needed?: number) => void;
  /** A saved record of one of this brand's styles landed (also for a style just switched away from). */
  onRecord?: (record: ArtStyle) => void;
  /** Something other styles' cards show may have changed (an extraction or activation can pause another
   *  style; a sample landed for some style): re-read the list. */
  onListStale?: () => void;
}

export interface UseArtStyle {
  loading: boolean;
  loadFailure: ArtStyleLoadFailure | null;
  /** The saved record with the owner's unsaved edits on top. */
  style: ArtStyle | undefined;
  saveState: SaveState;
  pending: PendingImage[];
  busy: ArtStyleBusy;
  /** Lumio is extracting the open style: its request is on its way, or its extraction job is queued or running.
   *  Read from the shared jobs feed, so it holds across a reload, a return visit and a style switch. */
  extracting: boolean;
  /** Per slot, the card waits for a draw that is on its way but has no job of its own yet (see `startingSlots`). */
  startingSlots: Readonly<Record<ArtStyleSlot, boolean>>;
  /** Whether the last swap was an undo (so the control offers Re-apply). */
  undone: boolean;
  /** Bumps each time a fresh extraction lands this session (drives its reveal). */
  revealSeq: number;
  /** The newest sample job per slot. */
  slotJobs: Partial<Record<ArtStyleSlot, CreativeJob>>;
  /** On the casino_creation pipeline, the preview batch the samples panel shows (see `previewsView`); null on the
   *  local pipeline. */
  previewsView: PreviewsView | null;
  /** The mascot Lumio is describing right now (one at a time), else null. */
  describingId: string | null;
  /** Per mascot id, bumps each time Lumio's suggested name lands this session (drives its reveal). */
  nameReveals: Readonly<Record<string, number>>;
  edit: (patch: ArtStylePatch) => void;
  /** Add images to the mood board, or (with `mascotId`) to that mascot. */
  addImages: (kind: ArtStyleImageKind, files: File[], mascotId?: string) => void;
  removeImage: (kind: ArtStyleImageKind, index: number, mascotId?: string) => void;
  /** A new, empty mascot at the end of the cast (none past {@link MAX_MASCOTS}); returns its id. */
  addMascot: () => string | null;
  removeMascot: (mascotId: string) => void;
  renameMascot: (mascotId: string, name: string) => void;
  /** Have Lumio name the mascot again (it names only an unnamed one, so the name is cleared first). */
  suggestMascotName: (mascotId: string) => void;
  /** Queue the style's extraction (its job then draws the four samples). The caller runs the affordability gate. */
  extract: () => Promise<void>;
  /** The newest extraction of the open style failed after its last good one: the record still holds the earlier
   *  style (an inherited one, for a duplicate), not the latest ingredients. */
  extractFailed: boolean;
  undo: () => Promise<void>;
  drawSamples: (slots?: readonly ArtStyleSlot[]) => Promise<void>;
  /** Use the style for the brand's draws (or pause it); true once the service did. */
  setActive: (active: boolean) => Promise<boolean>;
  retryLoad: () => void;
  retrySave: () => void;
  /** Save the pending edits now (before a duplicate reads the saved record). */
  flush: () => Promise<boolean>;
  /** Drop the pending autosave timer (the style is about to be deleted, so nothing is sent to it). */
  cancelPendingSave: () => void;
  /** Hold a record just created (a new style, a duplicate) so selecting it shows at once. */
  prime: (record: ArtStyle) => void;
}

const MAX_OF: Record<ArtStyleImageKind, number> = { mood: MAX_MOOD_IMAGES, mascot: MAX_MASCOT_IMAGES };

/** A new mascot's id, minted here so the cast keeps stable ids while it is unsaved (the service keeps it). */
const newMascotId = (): string => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // randomUUID needs a secure context; getRandomValues does not. Same RFC 4122 v4 shape.
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/** What a describe of this mascot read: its id and its images. Lumio looks at a mascot once per image set this
 *  session (the service drops a mascot's brief when its images change, so new images are worth a new look). */
const describeKey = (mascot: ArtStyleMascot): string =>
  `${mascot.id}|${mascot.images.map((image) => image.url).join('|')}`;

/** Whether Lumio should look at a saved mascot: it has images but no name or no brief yet. */
const needsDescribe = (mascot: ArtStyleMascot): boolean =>
  mascot.images.length > 0 && (mascot.name.trim() === '' || mascot.brief.trim() === '');

/** The failed extractions whose failure the owner was already told about, kept across reloads so each one is said
 *  once (a failed job stays the style's newest until the next extraction). Bounded; job ids are unique. */
const SHOWN_FAILURES_KEY = 'nova.artStyle.extractFailuresShown';
const SHOWN_FAILURES_MAX = 50;

const shownFailures = (): string[] => {
  try {
    const raw = window.localStorage.getItem(SHOWN_FAILURES_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

const markFailureShown = (jobId: string): void => {
  try {
    const next = [...shownFailures().filter((id) => id !== jobId), jobId].slice(-SHOWN_FAILURES_MAX);
    window.localStorage.setItem(SHOWN_FAILURES_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable (private mode, blocked): the in-memory set still says it once per visit.
  }
};

type SaveSnapshot = {
  tenant: number;
  style: string;
  gen: number;
  /** The owner's edits this save carries (compared back after it lands, to keep newer edits dirty). */
  sent: ArtStylePatch;
  /** `sent` plus the logo snapshot when it changed. */
  patch: ArtStylePatch;
  revision: number | undefined;
};

/**
 * One of the brand's art styles and every action on it. Keys everything on (`tenantId`, `styleId`): a brand or
 * style switch flushes the pending autosave of the style being left, then drops its state and in-flight
 * results. Records seen this visit are kept per style, so switching back to one is instant (it re-reads in the
 * background). The owner's inputs autosave through a debounced partial PUT, one at a time, guarded by
 * `expected_revision`; a stale revision refetches and retries once. Explicit actions (extract, undo, samples,
 * activate) flush the pending save first, since they read the SAVED record.
 */
export const useArtStyle = ({
  tenantId,
  styleId,
  logoUrl,
  notify,
  onNeedCredits,
  onRecord,
  onListStale,
}: UseArtStyleOptions): UseArtStyle => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [record, setRecord] = React.useState<ArtStyle | undefined>(undefined);
  const [dirty, setDirty] = React.useState<ArtStylePatch>({});
  const [loading, setLoading] = React.useState(true);
  const [loadFailure, setLoadFailure] = React.useState<ArtStyleLoadFailure | null>(null);
  const [saveState, setSaveState] = React.useState<SaveState>('idle');
  const [pending, setPending] = React.useState<PendingImage[]>([]);
  const [busy, setBusy] = React.useState<ArtStyleBusy>(null);
  const [undone, setUndone] = React.useState(false);
  const [revealSeq, setRevealSeq] = React.useState(0);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [describingId, setDescribingId] = React.useState<string | null>(null);
  const [nameReveals, setNameReveals] = React.useState<Record<string, number>>({});

  const recordRef = React.useRef<ArtStyle | undefined>(undefined);
  const dirtyRef = React.useRef<ArtStylePatch>({});
  const pendingRef = React.useRef<PendingImage[]>([]);
  const busyRef = React.useRef<ArtStyleBusy>(null);
  const genRef = React.useRef(0);
  const chainRef = React.useRef<Promise<boolean>>(Promise.resolve(true));
  const timerRef = React.useRef<number | undefined>(undefined);
  const tenantRef = React.useRef(tenantId);
  tenantRef.current = tenantId;
  const styleRef = React.useRef(styleId);
  styleRef.current = styleId;
  const logoRef = React.useRef(logoUrl);
  logoRef.current = logoUrl;
  const notifyRef = React.useRef(notify);
  notifyRef.current = notify;
  const onNeedCreditsRef = React.useRef(onNeedCredits);
  onNeedCreditsRef.current = onNeedCredits;
  const onRecordRef = React.useRef(onRecord);
  onRecordRef.current = onRecord;
  const onListStaleRef = React.useRef(onListStale);
  onListStaleRef.current = onListStale;
  /** The last record seen per style this visit (dropped on a brand switch). */
  const cacheRef = React.useRef<Map<string, ArtStyle>>(new Map());
  /** The mascot image sets Lumio has looked at (or was asked to) this visit, by {@link describeKey}. Kept across
   *  style and brand switches: mascot ids are unique, and a failed look is not retried on its own. */
  const describedRef = React.useRef<Set<string>>(new Set());

  /** Adopt a server record of the selected style unless it is older than the one held (responses can land
   *  out of order). Every record also updates its card in the list. */
  const applyRecord = React.useCallback((next: ArtStyle): void => {
    onRecordRef.current?.(next);
    const cached = cacheRef.current.get(next.id);
    if (!cached || next.revision >= cached.revision) cacheRef.current.set(next.id, next);
    if (next.id !== styleRef.current) return;
    const current = recordRef.current;
    if (current && next.revision < current.revision) return;
    recordRef.current = next;
    setRecord(next);
  }, []);

  // State + its ref mirror, written together (async callbacks read the ref, render reads the state).
  const writeDirty = React.useCallback((next: ArtStylePatch): void => {
    dirtyRef.current = next;
    setDirty(next);
  }, []);
  const writePending = React.useCallback((next: PendingImage[]): void => {
    pendingRef.current = next;
    setPending(next);
  }, []);
  const writeBusy = React.useCallback((next: ArtStyleBusy): void => {
    busyRef.current = next;
    setBusy(next);
  }, []);

  /** What a save would send right now. */
  const snapshot = React.useCallback((): SaveSnapshot | null => {
    const tenant = tenantRef.current;
    const current = recordRef.current;
    if (!tenant || !current) return null;
    const sent = { ...dirtyRef.current };
    const logo = logoRef.current;
    const patch: ArtStylePatch = logo && logo !== current.logo_url ? { ...sent, logo_url: logo } : sent;
    return { tenant, style: current.id, gen: genRef.current, sent, patch, revision: current.revision };
  }, []);

  /** The cast as the owner currently sees it (unsaved edit, else the saved one). */
  const castNow = React.useCallback(
    (): ArtStyleMascot[] => dirtyRef.current.mascots ?? recordRef.current?.mascots ?? [],
    [],
  );

  /** An image list as the owner currently sees it: the mood board, or one mascot's (undefined once that mascot
   *  is gone). */
  const listOf = React.useCallback(
    (kind: ArtStyleImageKind, mascotId?: string): ArtStyleImage[] | undefined =>
      kind === 'mood'
        ? (dirtyRef.current.mood_images ?? recordRef.current?.mood_images ?? [])
        : castNow().find((mascot) => mascot.id === mascotId)?.images,
    [castNow],
  );

  const runSave = React.useCallback(
    async (snap: SaveSnapshot | null): Promise<boolean> => {
      if (!snap || Object.keys(snap.patch).length === 0) return true;
      const live = (): boolean => snap.gen === genRef.current;
      if (live()) setSaveState('saving');
      let result = await saveArtStyle(snap.tenant, snap.style, snap.patch, snap.revision);
      if (!result.ok && result.reason === 'stale') {
        // Someone (another tab) saved first: take their revision, then put the owner's own edits on top.
        const fresh = await fetchArtStyle(snap.tenant, snap.style);
        if (fresh.ok) {
          if (live()) applyRecord(fresh.value);
          result = await saveArtStyle(snap.tenant, snap.style, snap.patch, fresh.value.revision);
        }
      }
      if (!live()) {
        // A style switched away from (or a brand left): its card and its cached record take what was saved, so
        // switching back shows the saved text (and the next edit builds on it, not on the pre-save record).
        // applyRecord leaves the open editor alone unless it is that style again and the record is newer.
        if (result.ok && snap.tenant === tenantRef.current) applyRecord(result.value);
        return result.ok;
      }
      if (result.ok) {
        applyRecord(result.value);
        const rest: ArtStylePatch = { ...dirtyRef.current };
        (Object.keys(snap.sent) as (keyof ArtStylePatch)[]).forEach((key) => {
          if (rest[key] === snap.sent[key]) delete rest[key];
        });
        writeDirty(rest);
        setSaveState(Object.keys(rest).length > 0 ? 'saving' : 'saved');
        return true;
      }
      setSaveState('error');
      return false;
    },
    [applyRecord, writeDirty],
  );

  /** Queue a save behind any in flight; it snapshots the edits when its turn comes. */
  const saveNow = React.useCallback((): Promise<boolean> => {
    const gen = genRef.current;
    window.clearTimeout(timerRef.current);
    timerRef.current = undefined;
    const next = chainRef.current.then(() => (gen === genRef.current ? runSave(snapshot()) : false));
    chainRef.current = next;
    return next;
  }, [runSave, snapshot]);

  const scheduleSave = React.useCallback((): void => {
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = undefined;
      void saveNow();
    }, ART_STYLE.AUTOSAVE_MS);
  }, [saveNow]);

  /* ── load (and reset on a brand or style switch) ────────────────────────── */

  // A brand switch forgets the previous brand's records.
  React.useEffect(() => {
    cacheRef.current = new Map();
  }, [tenantId]);

  React.useEffect(() => {
    genRef.current += 1;
    const gen = genRef.current;
    // A style seen before this visit shows at once (and re-reads below); an unseen one loads.
    const cached = styleId ? cacheRef.current.get(styleId) : undefined;
    recordRef.current = cached;
    setRecord(cached);
    writeDirty({});
    writePending([]);
    writeBusy(null);
    setSaveState('idle');
    setUndone(false);
    setLoadFailure(null);
    setDescribingId(null);
    chainRef.current = Promise.resolve(true);
    if (!tenantId || !styleId) {
      // No brand: nothing to load. A brand but no style yet: the list is still choosing one.
      setLoading(Boolean(tenantId));
      return undefined;
    }
    setLoading(!cached);
    const controller = new AbortController();
    void fetchArtStyle(tenantId, styleId, controller.signal).then((result) => {
      if (gen !== genRef.current) return;
      if (result.ok) applyRecord(result.value);
      else if (!cached) {
        setLoadFailure(
          isMissingStyle(result) ? 'missing' : result.reason === 'unavailable' ? 'unavailable' : 'failed',
        );
      }
      setLoading(false);
    });
    return () => {
      controller.abort();
      // Leaving (switching style or brand) with edits not yet saved: send them now, so the last keystrokes are never
      // lost. Not only an edit waiting for its debounce: one queued behind a slow step (a mascot description) or left
      // by a failed save has no timer, and its queued save gives up once the selection changed. Built from this
      // style's refs synchronously, before the next style's reset (the tenant/style/logo refs already hold the NEXT
      // selection here, so they are not read).
      const current = recordRef.current;
      if (Object.keys(dirtyRef.current).length > 0 && current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = undefined;
        const sent = { ...dirtyRef.current };
        void runSave({ tenant: tenantId, style: current.id, gen, sent, patch: sent, revision: current.revision });
      }
    };
  }, [tenantId, styleId, reloadKey, applyRecord, runSave, writeDirty, writePending, writeBusy]);

  /** Re-read the record (a sample landed). Unsaved edits stay on top of it. */
  const refresh = React.useCallback((): void => {
    const tenant = tenantRef.current;
    const style = styleRef.current;
    const gen = genRef.current;
    if (!tenant || !style) return;
    void fetchArtStyle(tenant, style).then((result) => {
      if (gen === genRef.current && result.ok) applyRecord(result.value);
    });
  }, [applyRecord]);

  /* ── sample jobs ─────────────────────────────────────────────────────────── */

  const jobsById = useAppSelector(selectCreativeJobsMap);
  const jobsRef = React.useRef(jobsById);
  jobsRef.current = jobsById;
  const slotJobs = React.useMemo(
    () => latestSampleJobs(jobsById, tenantId, styleId),
    [jobsById, tenantId, styleId],
  );
  // The open style's newest extraction says whether it is being extracted (from any tab, before a reload) and which
  // cards wait for the samples it queued.
  const extractJob = React.useMemo(
    () => latestExtractJob(jobsById, tenantId, styleId),
    [jobsById, tenantId, styleId],
  );
  // On the casino_creation pipeline the samples are a preview batch: the record says which previews an extraction
  // queued (their jobs' parent is the batch, not the extraction), so no slot waits on the extraction's children.
  const casinoCreation = record?.pipeline === 'casino_creation';
  const startingSlots = React.useMemo(
    () => startingSlotsOf(casinoCreation ? undefined : extractJob, jobsById, tenantId, styleId),
    [casinoCreation, extractJob, jobsById, tenantId, styleId],
  );
  // The extraction seen running here whose record re-read is on its way: the style still reads as extracting until
  // its definition lands, so the idle state (and a second Extract) never shows in between.
  const [rereadFor, setRereadFor] = React.useState<string | null>(null);
  const extracting =
    busy === 'extract' || isActiveJob(extractJob) || (extractJob !== undefined && rereadFor === extractJob.id);
  /** The style's newest extraction failed after its last good one (or before any): what the record holds is not
   *  what the owner last asked for, so "Ingredients changed" would mislead. */
  const extractFailed =
    !extracting &&
    record !== undefined &&
    record.id === styleId &&
    extractFailedSince(extractJob, record.extraction?.at);
  /** The extraction finished having queued its samples, and some of them are not in the jobs feed yet. */
  const awaitingSamples = extractJob?.status === 'done' && ART_STYLE_SLOTS.some((slot) => startingSlots[slot]);
  // The preview batch the panel shows: the record's, moved on by its follower jobs, or a newer one only its jobs know
  // yet. An extraction draws a new batch, so while it runs every card draws.
  const recordPreviews = record?.previews ?? null;
  const previewsView = React.useMemo(
    () => (casinoCreation ? previewsViewOf(recordPreviews, jobsById, tenantId, styleId, extracting) : null),
    [casinoCreation, recordPreviews, jobsById, tenantId, styleId, extracting],
  );
  const previewsBehind = previewsView
    ? previewsBehindKey(previewsView, recordPreviews, jobsById, tenantId, styleId)
    : '';

  // A draw or an extraction seen in flight this session that has now ended. A draw: done -> re-read the list
  // (the style's thumbnail) and, when it drew for the selected style, the record (the worker appended the
  // sample); failed -> the charge was refunded, so re-read the wallet. An extraction: done -> re-read the list
  // (the card's definition, its name, the style it made active) and, when it queued the samples, the wallet
  // (it charged them); on the open style the definition reveals, and samples that could not start say why. Jobs that
  // ended before this page opened are already in what it loaded. Tracked per brand, not per style, so a job
  // started on one style still lands after a switch.
  const seenActiveRef = React.useRef<Set<string>>(new Set());
  const settledRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    seenActiveRef.current = new Set();
    settledRef.current = new Set();
  }, [tenantId]);
  React.useEffect(() => {
    let landed = false;
    let landedHere = false;
    let walletStale = false;
    let relist = false;
    let extractedHere: CreativeJob | undefined;
    for (const job of Object.values(jobsById)) {
      const sample = isSampleJobFor(job, tenantId);
      if (!sample && !isExtractJobFor(job, tenantId)) continue;
      if (isActiveJob(job)) {
        seenActiveRef.current.add(job.id);
        continue;
      }
      if (!seenActiveRef.current.has(job.id) || settledRef.current.has(job.id)) continue;
      settledRef.current.add(job.id);
      // Events carry no created_at, so a job only seen live would rank newest for good: a list read stamps it, so
      // a retry or a redraw after it ranks above it. A done extraction that queued its samples is read below (on the
      // local pipeline, while its slots wait for their jobs; a preview batch waits on nothing, so it is read here).
      if (
        sample ||
        job.status !== 'done' ||
        job.stage !== EXTRACT_SAMPLES_QUEUED ||
        recordRef.current?.pipeline === 'casino_creation'
      ) {
        relist = true;
      }
      const here = job.root_banner_id === styleRef.current;
      if (sample) {
        if (job.status === 'done') {
          landed = true;
          if (here) landedHere = true;
        }
        if (job.status === 'failed') walletStale = true;
        continue;
      }
      // An extraction. Its record re-read (and a failure's message) follow from the job itself, below.
      if (job.status !== 'done') continue;
      landed = true;
      if (job.stage === EXTRACT_SAMPLES_QUEUED) walletStale = true;
      if (here) extractedHere = job;
    }
    if (landedHere) refresh();
    if (landed) onListStaleRef.current?.();
    if (walletStale) void dispatch(fetchOwnerWallets());
    if (relist) void dispatch(fetchCreativeJobs());
    if (extractedHere) {
      setUndone(false);
      setRevealSeq((n) => n + 1);
      if (extractedHere.stage === EXTRACT_SAMPLES_NOT_STARTED) {
        if (extractedHere.error === EXTRACT_SAMPLES_NO_CREDITS) onNeedCreditsRef.current();
        else if (extractedHere.error === ART_STYLE_RATING_NEEDED) {
          notifyRef.current(t('Lumio needs to rate this style before it can draw samples. Extract it again to rate it.'));
        } else if (extractedHere.error === PREVIEWS_RUNNING) {
          // The earlier batch is still drawing: Regenerate samples opens once it ends (the footer is hidden now).
          notifyRef.current(t('Your samples are still drawing.'));
        } else if (extractedHere.error === PIPELINE_NOT_CONFIGURED) {
          notifyRef.current(t("Lumio can't reach the art service right now. Please try again later."));
        } else notifyRef.current(t("The samples couldn't be started. Please try again."));
      }
    }
  }, [jobsById, tenantId, refresh, dispatch, t]);

  // The open style's newest extraction finished after the record was read (while it ran, or before this page saw
  // it run, a reload racing the jobs feed): re-read the record, once per extraction, so its definition lands.
  // Derived from the job, so it holds however the page learnt that the job ended. An extraction seen running here
  // keeps the style extracting until the read lands; a failed read is retried a few times, then let go. A layout
  // effect, so the frame the job ends in is never painted idle before the hold takes over.
  const refreshedForRef = React.useRef<Set<string>>(new Set());
  React.useLayoutEffect(() => {
    if (!extractJob || !record || record.id !== styleId || !tenantId || !styleId) return;
    if (!extractLandedSince(extractJob, record.extraction?.at) || refreshedForRef.current.has(extractJob.id)) return;
    const jobId = extractJob.id;
    refreshedForRef.current.add(jobId);
    if (seenActiveRef.current.has(jobId)) setRereadFor(jobId);
    const release = (): void => setRereadFor((current) => (current === jobId ? null : current));
    const gen = genRef.current;
    let tries = 0;
    const attempt = (): void => {
      void fetchArtStyle(tenantId, styleId).then((result) => {
        if (gen !== genRef.current) {
          release();
          return;
        }
        if (result.ok) {
          applyRecord(result.value);
          release();
          return;
        }
        tries += 1;
        if (tries >= ART_STYLE.SAMPLES_REREAD_TRIES) release();
        else window.setTimeout(attempt, ART_STYLE.SAMPLES_REREAD_MS);
      });
    };
    attempt();
  }, [extractJob, record, styleId, tenantId, applyRecord]);

  // The open style's newest extraction failed after its last good one: say so, once per extraction, here or on the
  // first visit after it failed, never again on a later reload (a later extraction that worked keeps it quiet).
  const failureShownRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    if (!extractJob || !record || record.id !== styleId) return;
    if (!extractFailedSince(extractJob, record.extraction?.at) || failureShownRef.current.has(extractJob.id)) return;
    failureShownRef.current.add(extractJob.id);
    if (shownFailures().includes(extractJob.id)) return;
    markFailureShown(extractJob.id);
    // An extraction is free and its samples are charged only once it succeeds, so a failed one spent nothing: say so.
    if (extractJob.error === EXTRACT_REFUSED) {
      notifyRef.current(
        t(
          "Lumio couldn't read a style from these ingredients. No credits were used. Try other mood board images or a different direction.",
        ),
      );
    } else if (extractJob.error === PIPELINE_NOT_CONFIGURED) {
      notifyRef.current(
        t("Lumio can't reach the art service right now. No credits were used. Please try again later."),
      );
    } else notifyRef.current(t("The style couldn't be extracted. No credits were used. Please try again."));
  }, [extractJob, record, styleId, t]);

  // The samples an extraction queued reach the jobs feed only through a list read (no event announces them until
  // a worker picks one up): re-read it as soon as the extraction is done, then a few more times while a card is
  // still waiting, so one failed read never leaves the cards drawing for nothing.
  React.useEffect(() => {
    if (!awaitingSamples) return undefined;
    void dispatch(fetchCreativeJobs());
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      if (tries >= ART_STYLE.SAMPLES_REREAD_TRIES) window.clearInterval(timer);
      void dispatch(fetchCreativeJobs());
    }, ART_STYLE.SAMPLES_REREAD_MS);
    return () => window.clearInterval(timer);
  }, [awaitingSamples, dispatch]);

  // The record is behind its previews' jobs (a batch only the jobs know yet, a follower job ended on a preview the
  // record still shows drawing): a fallback to the re-read a landed job or a "Regenerate samples" makes at once, so a
  // missed event or a read that raced the service's write never leaves a card drawing for nothing. A few tries, then
  // let go; a new key (another job ended) starts over.
  React.useEffect(() => {
    if (!previewsBehind) return undefined;
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      if (tries >= ART_STYLE.SAMPLES_REREAD_TRIES) window.clearInterval(timer);
      refresh();
    }, ART_STYLE.SAMPLES_REREAD_MS);
    return () => window.clearInterval(timer);
  }, [previewsBehind, refresh]);

  /* ── edits ──────────────────────────────────────────────────────────────── */

  const edit = React.useCallback(
    (patch: ArtStylePatch): void => {
      if (!recordRef.current) return;
      writeDirty({ ...dirtyRef.current, ...patch });
      setSaveState('saving');
      scheduleSave();
    },
    [scheduleSave, writeDirty],
  );

  const prepMessage = React.useCallback(
    (error: unknown): string => {
      const reason = error instanceof ImagePrepFailure ? error.reason : 'unreadable';
      if (reason === 'not_image') return t('That file is not an image.');
      if (reason === 'too_small') return t('That image is too small. Try a larger one.');
      if (reason === 'extreme_ratio') return t('That image is too long and thin. Try a less extreme shape.');
      return t("That image couldn't be read. Try another one.");
    },
    [t],
  );

  /** Replace one image list: the mood board, or one mascot's (dropped when that mascot was removed meanwhile). */
  const writeImages = React.useCallback(
    (kind: ArtStyleImageKind, mascotId: string | undefined, next: ArtStyleImage[]): void => {
      if (kind === 'mood') {
        edit({ mood_images: next });
        return;
      }
      const cast = castNow();
      if (!cast.some((mascot) => mascot.id === mascotId)) return;
      edit({ mascots: cast.map((mascot) => (mascot.id === mascotId ? { ...mascot, images: next } : mascot)) });
    },
    [castNow, edit],
  );

  const addOne = React.useCallback(
    async (
      kind: ArtStyleImageKind,
      file: File,
      tenant: number,
      style: string,
      gen: number,
      mascotId: string | undefined,
    ): Promise<void> => {
      let prepared;
      try {
        prepared = await prepareImage(file);
      } catch (error) {
        if (gen === genRef.current) notifyRef.current(prepMessage(error));
        return;
      }
      if (gen !== genRef.current) return;
      const key = `${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      writePending([...pendingRef.current, { key, dataUri: prepared.dataUri, kind, mascotId }]);
      const result = await uploadArtStyleImage(tenant, style, {
        data: prepared.base64,
        filename: prepared.filename,
        kind,
      });
      if (gen !== genRef.current) return;
      writePending(pendingRef.current.filter((p) => p.key !== key));
      if (!result.ok) {
        notifyRef.current(t("That image couldn't be uploaded. Please try again."));
        return;
      }
      const list = listOf(kind, mascotId);
      if (!list) return;
      writeImages(
        kind,
        mascotId,
        [...list, { url: result.value.url, name: file.name || prepared.filename }].slice(0, MAX_OF[kind]),
      );
    },
    [listOf, prepMessage, t, writeImages, writePending],
  );

  const addImages = React.useCallback(
    (kind: ArtStyleImageKind, files: File[], mascotId?: string): void => {
      const tenant = tenantRef.current;
      const current = recordRef.current;
      const list = listOf(kind, mascotId);
      if (!tenant || !current || !list) return;
      const uploading = pendingRef.current.filter((p) => p.kind === kind && p.mascotId === mascotId).length;
      const room = MAX_OF[kind] - list.length - uploading;
      if (room <= 0) return;
      if (files.length > room) notifyRef.current(t('Only {{n}} images fit here.', { n: MAX_OF[kind] }));
      files.slice(0, room).forEach((file) => void addOne(kind, file, tenant, current.id, genRef.current, mascotId));
    },
    [addOne, listOf, t],
  );

  const removeImage = React.useCallback(
    (kind: ArtStyleImageKind, index: number, mascotId?: string): void => {
      const list = listOf(kind, mascotId);
      if (list) writeImages(kind, mascotId, list.filter((_, i) => i !== index));
    },
    [listOf, writeImages],
  );

  /* ── the cast ───────────────────────────────────────────────────────────── */

  const addMascot = React.useCallback((): string | null => {
    const cast = castNow();
    if (!recordRef.current || cast.length >= MAX_MASCOTS) return null;
    const mascot: ArtStyleMascot = {
      id: newMascotId(),
      name: '',
      brief: '',
      images: [],
      created_at: new Date().toISOString(),
    };
    edit({ mascots: [...cast, mascot] });
    return mascot.id;
  }, [castNow, edit]);

  const removeMascot = React.useCallback(
    (mascotId: string): void => {
      edit({ mascots: castNow().filter((mascot) => mascot.id !== mascotId) });
      writePending(pendingRef.current.filter((p) => p.mascotId !== mascotId));
    },
    [castNow, edit, writePending],
  );

  const renameMascot = React.useCallback(
    (mascotId: string, name: string): void => {
      edit({ mascots: castNow().map((mascot) => (mascot.id === mascotId ? { ...mascot, name } : mascot)) });
    },
    [castNow, edit],
  );

  /**
   * Have Lumio look at one mascot's SAVED images: it writes the mascot's brief and, when the saved name is empty,
   * a name. Runs on the save chain (after the pending edits are saved, before any later save), so a save
   * snapshotted before the look lands can never send the old empty name over the suggestion; and one at a time.
   * The owner may keep editing meanwhile: an unsaved cast that still has no name for it takes the suggestion.
   * `manual` (the "Suggest a name" action) reports a failure; the automatic look stays quiet.
   */
  const describeMascot = React.useCallback(
    (mascotId: string, manual: boolean): Promise<boolean> => {
      const gen = genRef.current;
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
      const fail = (): void => {
        if (manual) notifyRef.current(t("A name couldn't be suggested. Please try again."));
      };
      const next = chainRef.current.then(async (): Promise<boolean> => {
        if (gen !== genRef.current) return false;
        const saved = await runSave(snapshot());
        const tenant = tenantRef.current;
        const style = recordRef.current?.id;
        if (gen !== genRef.current) return false;
        if (!saved || !tenant || !style) {
          fail();
          return saved;
        }
        const before = recordRef.current?.mascots.find((mascot) => mascot.id === mascotId);
        // Removed in the meantime, or nothing to look at yet.
        if (!before || before.images.length === 0) return true;
        describedRef.current.add(describeKey(before));
        setDescribingId(mascotId);
        const result = await describeArtStyleMascot(tenant, style, mascotId);
        if (gen !== genRef.current) return true;
        setDescribingId(null);
        if (!result.ok) {
          fail();
          return true;
        }
        const described = result.value.mascots.find((mascot) => mascot.id === mascotId);
        if (described) describedRef.current.add(describeKey(described));
        applyRecord(result.value);
        const suggested = described?.name.trim() ?? '';
        if (suggested && suggested !== before.name.trim()) {
          // The suggestion shows (and types in) only where the owner has not named it meanwhile: the saved cast
          // is what shows, or the unsaved one still has no name for it. A name they typed is never re-typed.
          const cast = dirtyRef.current.mascots;
          const entry = cast?.find((mascot) => mascot.id === mascotId);
          const takes = entry !== undefined && entry.name.trim() === '';
          if (cast && takes) {
            writeDirty({
              ...dirtyRef.current,
              mascots: cast.map((mascot) => (mascot.id === mascotId ? { ...mascot, name: suggested } : mascot)),
            });
          }
          if (!cast || takes) setNameReveals((prev) => ({ ...prev, [mascotId]: (prev[mascotId] ?? 0) + 1 }));
        }
        return true;
      });
      chainRef.current = next;
      return next;
    },
    [applyRecord, runSave, snapshot, t, writeDirty],
  );

  const suggestMascotName = React.useCallback(
    (mascotId: string): void => {
      if (!castNow().some((mascot) => mascot.id === mascotId && mascot.images.length > 0)) return;
      renameMascot(mascotId, '');
      void describeMascot(mascotId, true);
    },
    [castNow, renameMascot, describeMascot],
  );

  // Lumio looks at each saved mascot that has images but no name or brief yet (a first image just saved, a cast
  // from before the names), once per image set this visit. Queued on the save chain, so one at a time.
  React.useEffect(() => {
    if (!record) return;
    for (const mascot of record.mascots) {
      if (!needsDescribe(mascot) || describedRef.current.has(describeKey(mascot))) continue;
      describedRef.current.add(describeKey(mascot));
      void describeMascot(mascot.id, false);
    }
  }, [record, describeMascot]);

  /* ── explicit actions ────────────────────────────────────────────────────── */

  const failureCopy = React.useCallback(
    (failure: ArtStyleFailure, fallback: string): string => {
      if (failure.message === 'needs_mood_images') return t('Add at least one mood board image first.');
      if (failure.message === 'needs_definition') return t('Extract a style first.');
      // The casino_creation pipeline's refusals (a 503 keeps its code too).
      if (failure.message === ART_STYLE_RATING_NEEDED) {
        return t('Lumio needs to rate this style before it can draw samples. Extract it again to rate it.');
      }
      if (failure.message === PREVIEWS_RUNNING) return t('Your samples are still drawing.');
      if (failure.message === PIPELINE_NOT_CONFIGURED) {
        return t("Lumio can't reach the art service right now. Please try again later.");
      }
      if (failure.message === NEEDS_DEFINITION && recordRef.current?.pipeline === 'casino_creation') {
        return t('Extract your style again to draw new samples.');
      }
      if (failure.reason === 'unavailable') return t('Art styles are not available here yet.');
      return fallback;
    },
    [t],
  );

  /** Run one explicit action: flush the pending save, call, adopt the result. */
  const act = React.useCallback(
    async <T>(
      kind: Exclude<ArtStyleBusy, null>,
      call: (tenant: number, style: string) => Promise<{ ok: true; value: T } | ArtStyleFailure>,
      onDone: (value: T) => void,
      fallback: string,
    ): Promise<void> => {
      const tenant = tenantRef.current;
      const style = recordRef.current?.id;
      const gen = genRef.current;
      if (!tenant || !style || busyRef.current) return;
      writeBusy(kind);
      const saved = await saveNow();
      if (gen !== genRef.current) return;
      if (!saved) {
        writeBusy(null);
        notifyRef.current(t("Your latest changes couldn't be saved. Please try again."));
        return;
      }
      const result = await call(tenant, style);
      // An activation can change which style is active, so the other cards re-read, even when the owner has
      // already moved on to another style. (An extraction does so when its job ends.)
      if (result.ok && kind === 'activate') onListStaleRef.current?.();
      if (gen !== genRef.current) return;
      writeBusy(null);
      if (result.ok) {
        onDone(result.value);
        return;
      }
      if (result.reason === 'insufficient') {
        onNeedCredits(result.needed);
        return;
      }
      notifyRef.current(failureCopy(result, fallback));
    },
    [saveNow, failureCopy, onNeedCredits, t, writeBusy],
  );

  /** Queue the extraction. Its job goes into the jobs feed as soon as the request lands, for whichever style
   *  is open by then (the job is the style's, so a switch meanwhile still shows it on that style's card and on
   *  the style when the owner returns), and before the request's busy state clears, so `extracting` never
   *  blinks off between the two. Unstamped, like the sample draws below: it ranks newest until a list read. */
  const extract = React.useCallback(
    (): Promise<void> =>
      act(
        'extract',
        async (tenant, style) => {
          const result = await extractArtStyle(tenant, style);
          if (result.ok && !jobsRef.current[result.value.job_id]) {
            dispatch(
              upsertJob({
                id: result.value.job_id,
                tenant_id: String(tenant),
                root_banner_id: style,
                kind: ART_STYLE_EXTRACT_KIND,
                placement: ART_STYLE_EXTRACT_PLACEMENT,
                status: result.value.status,
              }),
            );
          }
          return result;
        },
        () => undefined,
        t("The style couldn't be extracted. Please try again."),
      ),
    [act, dispatch, t],
  );

  const undo = React.useCallback(
    (): Promise<void> =>
      act(
        'undo',
        (tenant, style) => undoArtStyle(tenant, style),
        (value) => {
          applyRecord(value);
          setUndone((u) => !u);
        },
        t("That couldn't be undone. Please try again."),
      ),
    [act, applyRecord, t],
  );

  const drawSamples = React.useCallback(
    (slots?: readonly ArtStyleSlot[]): Promise<void> =>
      act(
        'samples',
        async (tenant, style) => {
          const result = await drawArtStyleSamples(tenant, style, slots);
          // Refused for what the record would have shown (a batch another tab started, a rating it lost): the refusal
          // says so, and the record is read again so the panel shows it too.
          if (!result.ok && (result.message === PREVIEWS_RUNNING || result.message === ART_STYLE_RATING_NEEDED)) {
            refresh();
          }
          return result;
        },
        (run) => {
          const tenant = tenantRef.current;
          const style = styleRef.current;
          // Show the cards drawing at once (a fast `creative_job` push may already have them). The style id is
          // the job's write-once identity (`root_banner_id`), so it is set here, not left for the push. No
          // created_at: like a job only seen live it ranks newest until a list read stamps it with the service's
          // time (the browser's clock is not compared against the service's). A preview batch's followers also carry
          // the batch as their (write-once) parent, which is how the panel knows the batch before the record does.
          for (const job of run.jobs) {
            if (jobsRef.current[job.job_id]) continue;
            dispatch(
              upsertJob({
                id: job.job_id,
                tenant_id: String(tenant ?? ''),
                root_banner_id: style,
                kind: ART_STYLE_SAMPLE_KIND,
                placement: job.slot,
                ...(run.batch_id ? { parent_id: run.batch_id } : {}),
                status: 'queued',
              }),
            );
          }
          void dispatch(fetchOwnerWallets());
          // The record's `previews` is the batch now: read it, so the cards follow the new batch at once.
          if (run.batch_id) refresh();
        },
        t("The samples couldn't be started. Please try again."),
      ),
    [act, dispatch, refresh, t],
  );

  const setActive = React.useCallback(
    async (active: boolean): Promise<boolean> => {
      let done = false;
      await act(
        'activate',
        (tenant, style) => activateArtStyle(tenant, style, active),
        (value) => {
          done = true;
          applyRecord(value);
          notifyRef.current(active ? t('Style in use. New banners and images follow it.') : t('Style paused.'));
        },
        active
          ? t("The style couldn't be turned on. Please try again.")
          : t("The style couldn't be paused. Please try again."),
      );
      return done;
    },
    [act, applyRecord, t],
  );

  const retryLoad = React.useCallback((): void => setReloadKey((n) => n + 1), []);
  const retrySave = React.useCallback((): void => {
    void saveNow();
  }, [saveNow]);
  const cancelPendingSave = React.useCallback((): void => {
    window.clearTimeout(timerRef.current);
    timerRef.current = undefined;
  }, []);
  const prime = React.useCallback((next: ArtStyle): void => {
    if (!cacheRef.current.has(next.id)) cacheRef.current.set(next.id, next);
  }, []);

  // An unsaved "mature" choice is the owner's (the service records it with that source once it lands).
  const style = React.useMemo(
    (): ArtStyle | undefined =>
      record
        ? { ...record, ...dirty, ...(dirty.mature !== undefined ? { mature_source: 'owner' as const } : {}) }
        : undefined,
    [record, dirty],
  );

  return {
    loading,
    loadFailure,
    style,
    saveState,
    pending,
    busy,
    extracting,
    startingSlots,
    undone,
    revealSeq,
    slotJobs,
    previewsView,
    describingId,
    nameReveals,
    edit,
    addImages,
    removeImage,
    addMascot,
    removeMascot,
    renameMascot,
    suggestMascotName,
    extract,
    extractFailed,
    undo,
    drawSamples,
    setActive,
    retryLoad,
    retrySave,
    flush: saveNow,
    cancelPendingSave,
    prime,
  };
};

export default useArtStyle;
